/**
 * Admin - Inspections Overview
 * Professional design with card-based layout
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Card,
  Box,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  ButtonGroup,
} from "@mui/material";
import Icon from "@mui/material/Icon";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";

// Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";

// API
import { adminAPI } from "utils/api";

const localizer = momentLocalizer(moment);

function Inspections() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [inspections, setInspections] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [filteredInspections, setFilteredInspections] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    inProgress: 0,
    completed: 0,
    rejected: 0,
  });

  // Dialog states
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [inspectors, setInspectors] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [dialogMessage, setDialogMessage] = useState({ type: "", text: "" });

  // Calendar states
  const [inspectorCalendar, setInspectorCalendar] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [calendarView, setCalendarView] = useState("week");
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [hoverModalOpen, setHoverModalOpen] = useState(false);
  const [hoveredEvent, setHoveredEvent] = useState(null);
  const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });

  // Form data for reschedule/reassign
  const [formData, setFormData] = useState({
    inspectorId: "",
    inspectionDate: "",
    inspectionLocation: "",
  });

  useEffect(() => {
    fetchInspections();
    fetchInspectors();
  }, []);

  const fetchInspectors = async () => {
    try {
      const response = await adminAPI.getAvailableInspectors();
      setInspectors(response.inspectors || response.data || []);
    } catch (error) {
      console.error("Error fetching inspectors:", error);
    }
  };

  useEffect(() => {
    if (statusFilter === "all") {
      setFilteredInspections(inspections);
    } else if (statusFilter === "Inspection") {
      setFilteredInspections(
        inspections.filter((i) =>
          ["Inspection", "First_Inspection", "Second_Inspection"].includes(i.status)
        )
      );
    } else if (statusFilter === "Inspection_Completed") {
      setFilteredInspections(
        inspections.filter((i) =>
          ["Inspection_Completed", "Second_Inspection_Completed"].includes(i.status)
        )
      );
    } else {
      setFilteredInspections(inspections.filter((i) => i.status === statusFilter));
    }
  }, [statusFilter, inspections]);

  const fetchInspections = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAllProperties();
      const propertiesList = response.properties || response.data || [];

      // Filter properties that have inspections or are in inspection-related statuses
      // First inspection: First_Inspection (with or without inspector)
      // Second inspection: Second_Inspection (with or without inspector - admin assigns date/inspector here)
      // Legacy: "Inspection" with assignedInspector; Completed: Inspection_Completed, Second_Inspection_Completed, Rejected
      const inspectionProperties = propertiesList.filter((p) => {
        if (["First_Inspection", "Second_Inspection"].includes(p.status)) return true;
        if (p.status === "Inspection") return !!p.assignedInspector;
        if (["Inspection_Completed", "Second_Inspection_Completed", "Rejected"].includes(p.status))
          return true;
        return false;
      });

      // Transform to inspection format
      const inspectionsList = inspectionProperties.map((property) => ({
        id: property._id || property.id,
        propertyId: property._id || property.id,
        propertyTitle:
          typeof property.title === "object" && property.title?.en
            ? property.title.en
            : typeof property.title === "string"
            ? property.title
            : "N/A",
        propertyAddress:
          typeof property.address === "object" && property.address?.en
            ? property.address.en
            : typeof property.address === "string"
            ? property.address
            : "N/A",
        propertyCity:
          typeof property.city === "object" && property.city?.en
            ? property.city.en
            : typeof property.city === "string"
            ? property.city
            : "N/A",
        propertyType: property.propertyType,
        inspector: property.assignedInspector?.fullName || "N/A",
        inspectorId: property.assignedInspector?._id || property.assignedInspector,
        inspectionDate: property.inspectionDate,
        inspectionLocation: property.inspectionLocation,
        status: property.status,
        createdAt: property.createdAt,
      }));

      setInspections(inspectionsList);
      setFilteredInspections(inspectionsList);

      setStats({
        total: inspectionsList.length,
        pending: inspectionsList.filter((i) => ["Pending", "Request_Submission"].includes(i.status))
          .length,
        inProgress: inspectionsList.filter((i) =>
          ["Inspection", "First_Inspection", "Second_Inspection"].includes(i.status)
        ).length,
        completed: inspectionsList.filter((i) =>
          ["Inspection_Completed", "Second_Inspection_Completed"].includes(i.status)
        ).length,
        rejected: inspectionsList.filter((i) => i.status === "Rejected").length,
      });
    } catch (error) {
      console.error("Error fetching inspections:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Inspection":
      case "First_Inspection":
      case "Second_Inspection":
        return "info";
      case "Inspection_Completed":
      case "Second_Inspection_Completed":
        return "success";
      case "Pending":
      case "Request_Submission":
        return "warning";
      case "Rejected":
        return "error";
      default:
        return "secondary";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Inspection":
      case "First_Inspection":
        return "search";
      case "Second_Inspection":
        return "fact_check";
      case "Inspection_Completed":
      case "Second_Inspection_Completed":
        return "check_circle";
      case "Pending":
      case "Request_Submission":
        return "schedule";
      case "Rejected":
        return "cancel";
      default:
        return "help";
    }
  };

  const handleViewProperty = (propertyId) => {
    navigate(`/admin/properties/${propertyId}`);
  };

  // Fetch inspector calendar when inspector is selected (for reschedule/reassign)
  useEffect(() => {
    const fetchInspectorCalendar = async () => {
      let inspectorId = null;

      if (rescheduleDialogOpen && selectedInspection?.inspectorId) {
        inspectorId = selectedInspection.inspectorId;
      } else if (reassignDialogOpen && formData.inspectorId) {
        inspectorId = formData.inspectorId;
      }

      if (!inspectorId) {
        setInspectorCalendar(null);
        if (!rescheduleDialogOpen) {
          setSelectedSlot(null);
        }
        return;
      }

      try {
        const response = await adminAPI.getInspectorCalendar(inspectorId);
        setInspectorCalendar(response);

        if (rescheduleDialogOpen && selectedInspection?.inspectionDate) {
          // For reschedule, keep existing date if available
          const existingDate = new Date(selectedInspection.inspectionDate);
          existingDate.setMinutes(0, 0, 0);
          const endDate = new Date(existingDate);
          endDate.setHours(endDate.getHours() + 1);
          setSelectedSlot({ start: existingDate, end: endDate });
          setFormData((prev) => ({
            ...prev,
            inspectionDate: formatDateTimeLocal(existingDate),
          }));
        } else if (!rescheduleDialogOpen) {
          setSelectedSlot(null);
        }
      } catch (error) {
        console.error("Error fetching inspector calendar:", error);
        setDialogMessage({
          type: "error",
          text: "Failed to load inspector calendar. Please try again.",
        });
      }
    };

    if (rescheduleDialogOpen || reassignDialogOpen) {
      fetchInspectorCalendar();
    }
  }, [
    rescheduleDialogOpen ? selectedInspection?.inspectorId : formData.inspectorId,
    rescheduleDialogOpen,
    reassignDialogOpen,
  ]);

  // Helper function to format date for datetime-local input (local time)
  const formatDateTimeLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Check if selected date conflicts with inspector's schedule
  const checkDateConflict = (selectedDate, selectedEndDate) => {
    if (!inspectorCalendar || !inspectorCalendar.inspections || !selectedDate) {
      return null;
    }

    const selectedDateTime = new Date(selectedDate);
    const selectedEndTime = selectedEndDate
      ? new Date(selectedEndDate)
      : new Date(selectedDateTime);

    const conflict = inspectorCalendar.inspections.find((inspection) => {
      // Skip the current inspection being rescheduled/reassigned
      if (
        selectedInspection &&
        (inspection.id === selectedInspection.propertyId ||
          inspection._id === selectedInspection.propertyId)
      ) {
        return false;
      }

      const inspectionDate = new Date(inspection.inspectionDate);
      const inspectionEndTime = new Date(inspectionDate);
      inspectionEndTime.setHours(inspectionEndTime.getHours() + 1);

      // Check if there's an overlap
      return (
        (selectedDateTime >= inspectionDate && selectedDateTime < inspectionEndTime) ||
        (selectedEndTime > inspectionDate && selectedEndTime <= inspectionEndTime) ||
        (selectedDateTime <= inspectionDate && selectedEndTime >= inspectionEndTime)
      );
    });

    return conflict || null;
  };

  // Convert inspector inspections to calendar events
  const getCalendarEvents = () => {
    if (!inspectorCalendar || !inspectorCalendar.inspections) return [];

    const events = inspectorCalendar.inspections
      .map((inspection) => {
        // Skip the current inspection being rescheduled/reassigned
        if (
          selectedInspection &&
          (inspection.id === selectedInspection.propertyId ||
            inspection._id === selectedInspection.propertyId)
        ) {
          return null;
        }

        const startDate = new Date(inspection.inspectionDate);
        const endDate = new Date(startDate);
        endDate.setHours(endDate.getHours() + 1);

        const timeStr = startDate.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        });

        // Handle bilingual fields from API response
        const propertyTitle =
          typeof inspection.title === "object" && inspection.title?.en
            ? inspection.title.en
            : typeof inspection.title === "string"
            ? inspection.title
            : inspection.propertyTitle || `Inspection - ${timeStr}`;
        const propertyAddress =
          typeof inspection.address === "object" && inspection.address?.en
            ? inspection.address.en
            : typeof inspection.address === "string"
            ? inspection.address
            : inspection.propertyAddress || "N/A";
        const propertyCity =
          typeof inspection.city === "object" && inspection.city?.en
            ? inspection.city.en
            : typeof inspection.city === "string"
            ? inspection.city
            : inspection.propertyCity || "N/A";

        return {
          title: propertyTitle,
          start: startDate,
          end: endDate,
          resource: {
            id: inspection.id || inspection._id,
            title: propertyTitle,
            address: propertyAddress,
            city: propertyCity,
            location: inspection.inspectionLocation,
            status: inspection.status,
            isExisting: true,
          },
          inspection: {
            ...inspection,
            propertyTitle,
            propertyAddress,
            propertyCity,
          }, // Store full inspection object with extracted bilingual fields for hover modal
        };
      })
      .filter(Boolean);

    // Add selected slot as a temporary event for visual feedback
    if (selectedSlot) {
      events.push({
        title: "Selected Time",
        start: selectedSlot.start,
        end: selectedSlot.end,
        resource: {
          id: "selected",
          isSelected: true,
        },
      });
    }

    return events;
  };

  // Check if a slot is reserved
  const isSlotReserved = (slotStart, slotEnd) => {
    if (!inspectorCalendar || !inspectorCalendar.inspections) {
      return false;
    }

    return inspectorCalendar.inspections.some((inspection) => {
      // Skip the current inspection being rescheduled/reassigned
      if (
        selectedInspection &&
        (inspection.id === selectedInspection.propertyId ||
          inspection._id === selectedInspection.propertyId)
      ) {
        return false;
      }

      const inspectionStart = new Date(inspection.inspectionDate);
      const inspectionEnd = new Date(inspectionStart);
      inspectionEnd.setHours(inspectionEnd.getHours() + 1);

      // Check for overlap
      return (
        (slotStart >= inspectionStart && slotStart < inspectionEnd) ||
        (slotEnd > inspectionStart && slotEnd <= inspectionEnd) ||
        (slotStart <= inspectionStart && slotEnd >= inspectionEnd)
      );
    });
  };

  // Handle calendar slot selection
  const handleSelectSlot = ({ start, end }) => {
    const clickedTime = new Date(start);
    const selectedStart = new Date(clickedTime);
    selectedStart.setMinutes(0, 0, 0);
    const selectedEnd = new Date(selectedStart);
    selectedEnd.setHours(selectedEnd.getHours() + 1);

    // Check if the slot is already reserved
    if (isSlotReserved(selectedStart, selectedEnd)) {
      setDialogMessage({
        type: "error",
        text: `This time slot is already reserved. Please choose a different time.`,
      });
      setSelectedSlot(null);
      return;
    }

    // Check for conflicts
    const conflict = checkDateConflict(selectedStart.toISOString(), selectedEnd.toISOString());
    if (conflict) {
      setDialogMessage({
        type: "error",
        text: `This time slot conflicts with an existing inspection. Please choose a different time.`,
      });
      setSelectedSlot(null);
      return;
    }

    const formattedDateTime = formatDateTimeLocal(selectedStart);
    setSelectedSlot({ start: selectedStart, end: selectedEnd });
    setHoverModalOpen(false);
    setFormData((prev) => ({
      ...prev,
      inspectionDate: formattedDateTime,
    }));
    setDialogMessage({
      type: "success",
      text: `Time selected: ${selectedStart.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })} - ${selectedEnd.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })}`,
    });
  };

  // Handle calendar event click
  const handleSelectEvent = (event) => {
    if (event.resource?.isExisting) {
      setDialogMessage({
        type: "info",
        text: `This time slot is already reserved for: ${event.resource.title || "Inspection"}`,
      });
      return;
    }

    if (event.resource?.isSelected) {
      setSelectedSlot(null);
      setFormData((prev) => ({ ...prev, inspectionDate: "" }));
      setDialogMessage({ type: "", text: "" });
    }
  };

  // Slot prop getter
  const slotPropGetter = (date) => {
    if (!inspectorCalendar || !inspectorCalendar.inspections) {
      return {};
    }

    const slotStart = new Date(date);
    slotStart.setMinutes(0, 0, 0);
    const slotEnd = new Date(slotStart);
    slotEnd.setHours(slotEnd.getHours() + 1);

    const hasConflict = inspectorCalendar.inspections.some((inspection) => {
      // Skip the current inspection being rescheduled/reassigned
      if (
        selectedInspection &&
        (inspection.id === selectedInspection.propertyId ||
          inspection._id === selectedInspection.propertyId)
      ) {
        return false;
      }

      const inspectionStart = new Date(inspection.inspectionDate);
      inspectionStart.setMinutes(0, 0, 0);
      const inspectionEnd = new Date(inspectionStart);
      inspectionEnd.setHours(inspectionEnd.getHours() + 1);

      return (
        (slotStart.getTime() >= inspectionStart.getTime() &&
          slotStart.getTime() < inspectionEnd.getTime()) ||
        (slotEnd.getTime() > inspectionStart.getTime() &&
          slotEnd.getTime() <= inspectionEnd.getTime()) ||
        (slotStart.getTime() <= inspectionStart.getTime() &&
          slotEnd.getTime() >= inspectionEnd.getTime())
      );
    });

    if (hasConflict) {
      return {
        className: "rbc-slot-reserved",
        style: {
          backgroundColor: "rgba(244, 67, 54, 0.15)",
          border: "1px dashed rgba(244, 67, 54, 0.5)",
          cursor: "not-allowed",
          pointerEvents: "none",
          opacity: 0.7,
        },
      };
    }

    return {};
  };

  // Event style getter
  const eventStyleGetter = (event) => {
    if (event.resource?.isSelected) {
      return {
        style: {
          backgroundColor: "#4CAF50",
          borderRadius: "6px",
          opacity: 1,
          color: "white",
          border: "2px solid #2e7d32",
          boxShadow: "0 4px 12px rgba(76, 175, 80, 0.5)",
          display: "flex",
          alignItems: "center",
          padding: "8px 12px",
          fontWeight: 600,
          fontSize: "0.875rem",
          minHeight: "32px",
          zIndex: 10,
          position: "relative",
          whiteSpace: "normal",
          wordWrap: "break-word",
          lineHeight: "1.4",
          cursor: "pointer",
        },
      };
    }

    if (event.resource?.isExisting) {
      return {
        style: {
          backgroundColor: "#1976d2",
          borderRadius: "6px",
          opacity: 0.95,
          color: "white",
          border: "none",
          display: "flex",
          alignItems: "center",
          padding: "6px 10px",
          fontWeight: 500,
          fontSize: "0.875rem",
          minHeight: "28px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          lineHeight: "1.4",
          cursor: "pointer",
          pointerEvents: "auto",
        },
      };
    }

    return {
      style: {
        backgroundColor: "#1976d2",
        borderRadius: "6px",
        opacity: 0.95,
        color: "white",
        border: "none",
        display: "flex",
        alignItems: "center",
        padding: "6px 10px",
        fontWeight: 500,
        fontSize: "0.875rem",
        minHeight: "28px",
        whiteSpace: "normal",
        wordWrap: "break-word",
        lineHeight: "1.4",
      },
    };
  };

  // Handle view change
  const handleViewChange = (view) => {
    setCalendarView(view);
  };

  // Navigate calendar
  const navigateCalendar = (action) => {
    const newDate = new Date(calendarDate);
    if (calendarView === "month") {
      newDate.setMonth(
        action === "PREV" ? calendarDate.getMonth() - 1 : calendarDate.getMonth() + 1
      );
    } else if (calendarView === "week") {
      newDate.setDate(action === "PREV" ? calendarDate.getDate() - 7 : calendarDate.getDate() + 7);
    } else if (calendarView === "day") {
      newDate.setDate(action === "PREV" ? calendarDate.getDate() - 1 : calendarDate.getDate() + 1);
    }
    setCalendarDate(newDate);
  };

  const handleOpenReschedule = (inspection) => {
    setSelectedInspection(inspection);
    setFormData({
      inspectorId: inspection.inspectorId || "",
      inspectionDate: inspection.inspectionDate
        ? formatDateTimeLocal(new Date(inspection.inspectionDate))
        : "",
      inspectionLocation: inspection.inspectionLocation || "",
    });
    setDialogMessage({ type: "", text: "" });
    setSelectedSlot(null);
    setInspectorCalendar(null);
    setCalendarDate(new Date());
    setRescheduleDialogOpen(true);
  };

  const handleOpenReassign = (inspection) => {
    setSelectedInspection(inspection);
    setFormData({
      inspectorId: "",
      inspectionDate: inspection.inspectionDate
        ? formatDateTimeLocal(new Date(inspection.inspectionDate))
        : "",
      inspectionLocation: inspection.inspectionLocation || "",
    });
    setDialogMessage({ type: "", text: "" });
    setSelectedSlot(null);
    setInspectorCalendar(null);
    setCalendarDate(new Date());
    setReassignDialogOpen(true);
  };

  const handleCloseDialogs = () => {
    if (!submitting) {
      setRescheduleDialogOpen(false);
      setReassignDialogOpen(false);
      setSelectedInspection(null);
      setFormData({
        inspectorId: "",
        inspectionDate: "",
        inspectionLocation: "",
      });
      setDialogMessage({ type: "", text: "" });
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Update selected slot when date is manually changed
    if (name === "inspectionDate" && value) {
      const selectedDateTime = new Date(value);
      selectedDateTime.setMinutes(0, 0, 0);
      const endDateTime = new Date(selectedDateTime);
      endDateTime.setHours(endDateTime.getHours() + 1);
      setSelectedSlot({
        start: selectedDateTime,
        end: endDateTime,
      });
    } else if (name === "inspectionDate" && !value) {
      setSelectedSlot(null);
    }
  };

  const handleReschedule = async () => {
    if (!formData.inspectionDate || !formData.inspectionLocation) {
      setDialogMessage({
        type: "error",
        text: "Please fill in all required fields.",
      });
      return;
    }

    try {
      setSubmitting(true);
      setDialogMessage({ type: "", text: "" });

      const inspectionDateObj = new Date(formData.inspectionDate);
      const inspectionData = {
        inspectorId: selectedInspection.inspectorId, // Keep same inspector
        inspectionDate: inspectionDateObj.toISOString(),
        inspectionLocation: formData.inspectionLocation.trim(),
      };

      await adminAPI.assignInspector(selectedInspection.propertyId, inspectionData);

      setDialogMessage({
        type: "success",
        text: "Inspection rescheduled successfully!",
      });

      setTimeout(() => {
        handleCloseDialogs();
        fetchInspections(); // Refresh the list
      }, 1500);
    } catch (error) {
      console.error("Error rescheduling inspection:", error);
      setDialogMessage({
        type: "error",
        text: error.message || "Failed to reschedule inspection. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReassign = async () => {
    if (!formData.inspectorId || !formData.inspectionDate || !formData.inspectionLocation) {
      setDialogMessage({
        type: "error",
        text: "Please fill in all required fields.",
      });
      return;
    }

    try {
      setSubmitting(true);
      setDialogMessage({ type: "", text: "" });

      const inspectionDateObj = new Date(formData.inspectionDate);
      const inspectionData = {
        inspectorId: formData.inspectorId,
        inspectionDate: inspectionDateObj.toISOString(),
        inspectionLocation: formData.inspectionLocation.trim(),
      };

      await adminAPI.assignInspector(selectedInspection.propertyId, inspectionData);

      setDialogMessage({
        type: "success",
        text: "Inspector reassigned successfully!",
      });

      setTimeout(() => {
        handleCloseDialogs();
        fetchInspections(); // Refresh the list
      }, 1500);
    } catch (error) {
      console.error("Error reassigning inspector:", error);
      setDialogMessage({
        type: "error",
        text: error.message || "Failed to reassign inspector. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox
        py={3}
        sx={{
          height: "calc(100vh - 64px)",
          overflowY: "auto",
          overflowX: "hidden",
          "&::-webkit-scrollbar": {
            width: "10px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: "10px",
            "&:hover": {
              background: "#555",
            },
          },
        }}
      >
        {/* Header */}
        <MDBox mb={3}>
          <MDTypography variant="h4" fontWeight="bold" mb={0.5}>
            Inspections Overview
          </MDTypography>
          <MDTypography variant="body2" color="text">
            Monitor and manage all property inspections
          </MDTypography>
        </MDBox>

        {/* Statistics Cards */}
        <Grid container spacing={3} mb={3} sx={{ "& .MuiGrid-item": { display: "flex" } }}>
          <Grid item xs={12} sm={6} md={3}>
            <ComplexStatisticsCard
              color="dark"
              icon="assignment"
              title="Total Inspections"
              count={stats.total}
              percentage={{
                color: "success",
                amount: "",
                label: "all inspections",
              }}
              sx={{ width: "100%", height: "100%", minHeight: "180px" }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ComplexStatisticsCard
              color="info"
              icon="search"
              title="In Progress"
              count={stats.inProgress}
              percentage={{
                color: "info",
                amount:
                  stats.total > 0 ? `${Math.round((stats.inProgress / stats.total) * 100)}%` : "0%",
                label: "currently inspecting",
              }}
              sx={{ width: "100%", height: "100%", minHeight: "180px" }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ComplexStatisticsCard
              color="success"
              icon="check_circle"
              title="Completed"
              count={stats.completed}
              percentage={{
                color: "success",
                amount:
                  stats.total > 0 ? `${Math.round((stats.completed / stats.total) * 100)}%` : "0%",
                label: "inspections done",
              }}
              sx={{ width: "100%", height: "100%", minHeight: "180px" }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ComplexStatisticsCard
              color="error"
              icon="cancel"
              title="Rejected"
              count={stats.rejected}
              percentage={{
                color: "error",
                amount:
                  stats.total > 0 ? `${Math.round((stats.rejected / stats.total) * 100)}%` : "0%",
                label: "rejected inspections",
              }}
              sx={{ width: "100%", height: "100%", minHeight: "180px" }}
            />
          </Grid>
        </Grid>

        {/* Filter Chips */}
        <MDBox mb={3}>
          <Card>
            <MDBox p={2}>
              <MDTypography
                variant="caption"
                color="text.secondary"
                fontWeight="medium"
                mb={1}
                display="block"
              >
                Filter by Status
              </MDTypography>
              <MDBox display="flex" gap={1} flexWrap="wrap">
                <Chip
                  icon={<Icon>list</Icon>}
                  label={`All (${stats.total})`}
                  color={statusFilter === "all" ? "primary" : "default"}
                  onClick={() => setStatusFilter("all")}
                  sx={{ cursor: "pointer", fontWeight: statusFilter === "all" ? 600 : 400 }}
                />
                <Chip
                  icon={<Icon>search</Icon>}
                  label={`In Progress (${stats.inProgress})`}
                  color={statusFilter === "Inspection" ? "info" : "default"}
                  onClick={() => setStatusFilter("Inspection")}
                  sx={{ cursor: "pointer", fontWeight: statusFilter === "Inspection" ? 600 : 400 }}
                />
                <Chip
                  icon={<Icon>check_circle</Icon>}
                  label={`Completed (${stats.completed})`}
                  color={statusFilter === "Inspection_Completed" ? "success" : "default"}
                  onClick={() => setStatusFilter("Inspection_Completed")}
                  sx={{
                    cursor: "pointer",
                    fontWeight: statusFilter === "Inspection_Completed" ? 600 : 400,
                  }}
                />
                <Chip
                  icon={<Icon>cancel</Icon>}
                  label={`Rejected (${stats.rejected})`}
                  color={statusFilter === "Rejected" ? "error" : "default"}
                  onClick={() => setStatusFilter("Rejected")}
                  sx={{ cursor: "pointer", fontWeight: statusFilter === "Rejected" ? 600 : 400 }}
                />
              </MDBox>
            </MDBox>
          </Card>
        </MDBox>

        {/* Inspections Grid */}
        {filteredInspections.length === 0 ? (
          <Card>
            <MDBox p={4} textAlign="center">
              <Icon sx={{ fontSize: "4rem", color: "text.secondary", mb: 2 }}>search_off</Icon>
              <MDTypography variant="h6" color="text.secondary" mb={1}>
                No Inspections Found
              </MDTypography>
              <MDTypography variant="body2" color="text.secondary">
                {statusFilter === "all"
                  ? "There are no inspections in the system yet."
                  : `No inspections with status "${statusFilter.replace("_", " ")}" found.`}
              </MDTypography>
            </MDBox>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {filteredInspections.map((inspection) => (
              <Grid item xs={12} md={6} lg={4} key={inspection.id}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 4,
                    },
                    borderRadius: 3,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  {/* Status Header */}
                  <MDBox
                    sx={{
                      bgcolor: `${getStatusColor(inspection.status)}.main`,
                      color: "white",
                      p: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <MDBox display="flex" alignItems="center" gap={1}>
                      <Icon sx={{ fontSize: "1.2rem" }}>{getStatusIcon(inspection.status)}</Icon>
                      <MDTypography variant="button" fontWeight="bold" color="white">
                        {inspection.status?.replace(/_/g, " ") || "Unknown"}
                      </MDTypography>
                    </MDBox>
                    <Tooltip title="View Property Details">
                      <IconButton
                        size="small"
                        onClick={() => handleViewProperty(inspection.propertyId)}
                        sx={{ color: "white", "&:hover": { bgcolor: "rgba(255,255,255,0.2)" } }}
                      >
                        <Icon>visibility</Icon>
                      </IconButton>
                    </Tooltip>
                  </MDBox>

                  {/* Content */}
                  <MDBox p={2.5} sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                    {/* Property Title */}
                    <MDTypography variant="h6" fontWeight="bold" mb={1} sx={{ lineHeight: 1.3 }}>
                      {inspection.propertyTitle || "N/A"}
                    </MDTypography>

                    {/* Property Type & Address */}
                    <MDBox mb={2}>
                      <MDBox display="flex" alignItems="center" gap={0.5} mb={0.5}>
                        <Icon sx={{ fontSize: "1rem", color: "text.secondary" }}>category</Icon>
                        <MDTypography
                          variant="caption"
                          color="text.secondary"
                          textTransform="capitalize"
                        >
                          {inspection.propertyType || "N/A"}
                        </MDTypography>
                      </MDBox>
                      <MDBox display="flex" alignItems="flex-start" gap={0.5}>
                        <Icon sx={{ fontSize: "1rem", color: "text.secondary", mt: 0.25 }}>
                          location_on
                        </Icon>
                        <MDTypography
                          variant="caption"
                          color="text.secondary"
                          sx={{ lineHeight: 1.4 }}
                        >
                          {inspection.propertyAddress || "N/A"}
                          {inspection.propertyCity && `, ${inspection.propertyCity}`}
                        </MDTypography>
                      </MDBox>
                    </MDBox>

                    <Divider sx={{ my: 1.5 }} />

                    {/* Inspector Info */}
                    <MDBox mb={2}>
                      <MDTypography
                        variant="caption"
                        color="text.secondary"
                        fontWeight="medium"
                        display="block"
                        mb={0.5}
                      >
                        Inspector
                      </MDTypography>
                      <MDBox display="flex" alignItems="center" gap={0.5}>
                        <Icon sx={{ fontSize: "1rem", color: "info.main" }}>person</Icon>
                        <MDTypography variant="body2" fontWeight="medium">
                          {inspection.inspector}
                        </MDTypography>
                      </MDBox>
                    </MDBox>

                    {/* Inspection Date & Time */}
                    {inspection.inspectionDate && (
                      <MDBox mb={2}>
                        <MDTypography
                          variant="caption"
                          color="text.secondary"
                          fontWeight="medium"
                          display="block"
                          mb={0.5}
                        >
                          Inspection Schedule
                        </MDTypography>
                        <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          <MDBox display="flex" alignItems="center" gap={0.5}>
                            <Icon sx={{ fontSize: "1rem", color: "info.main" }}>event</Icon>
                            <MDTypography variant="body2" fontWeight="medium">
                              {formatDate(inspection.inspectionDate)}
                            </MDTypography>
                          </MDBox>
                          <MDBox display="flex" alignItems="center" gap={0.5}>
                            <Icon sx={{ fontSize: "1rem", color: "info.main" }}>schedule</Icon>
                            <MDTypography variant="body2" fontWeight="medium">
                              {formatTime(inspection.inspectionDate)}
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                      </MDBox>
                    )}

                    {/* Inspection Location */}
                    {inspection.inspectionLocation && (
                      <MDBox>
                        <MDTypography
                          variant="caption"
                          color="text.secondary"
                          fontWeight="medium"
                          display="block"
                          mb={1}
                        >
                          Inspection Location
                        </MDTypography>
                        <MDButton
                          variant="gradient"
                          color="info"
                          size="small"
                          startIcon={<Icon sx={{ fontSize: "1.1rem", color: "white" }}>place</Icon>}
                          endIcon={
                            <Icon sx={{ fontSize: "1rem", color: "white" }}>open_in_new</Icon>
                          }
                          onClick={() => {
                            // Open Google Maps with the location
                            const encodedLocation = encodeURIComponent(
                              inspection.inspectionLocation
                            );
                            window.open(
                              `https://www.google.com/maps/search/?api=1&query=${encodedLocation}`,
                              "_blank"
                            );
                          }}
                          fullWidth
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: 2,
                            py: 1,
                            color: "white",
                            boxShadow: "0 2px 8px rgba(33, 150, 243, 0.2)",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: "0 4px 12px rgba(33, 150, 243, 0.3)",
                              color: "white",
                            },
                            "& .MuiButton-startIcon": {
                              marginRight: 1,
                            },
                            "& .MuiButton-endIcon": {
                              marginLeft: 0.5,
                            },
                          }}
                        >
                          <MDTypography
                            variant="button"
                            fontWeight="bold"
                            sx={{
                              color: "white",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "100%",
                            }}
                          >
                            Open in Maps
                          </MDTypography>
                        </MDButton>
                      </MDBox>
                    )}
                  </MDBox>

                  {/* Footer Actions */}
                  <MDBox
                    sx={{
                      p: 2,
                      bgcolor: "grey.50",
                      borderTop: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <MDButton
                          variant="outlined"
                          color="info"
                          size="small"
                          fullWidth
                          onClick={() => handleViewProperty(inspection.propertyId)}
                          startIcon={<Icon>visibility</Icon>}
                          sx={{ mb: 1 }}
                        >
                          View Details
                        </MDButton>
                      </Grid>
                      {inspection.status === "Inspection" && (
                        <>
                          <Grid item xs={6}>
                            <MDButton
                              variant="outlined"
                              color="warning"
                              size="small"
                              fullWidth
                              onClick={() => handleOpenReschedule(inspection)}
                              startIcon={<Icon>event</Icon>}
                            >
                              Reschedule
                            </MDButton>
                          </Grid>
                          <Grid item xs={6}>
                            <MDButton
                              variant="outlined"
                              color="secondary"
                              size="small"
                              fullWidth
                              onClick={() => handleOpenReassign(inspection)}
                              startIcon={<Icon>swap_horiz</Icon>}
                            >
                              Reassign
                            </MDButton>
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </MDBox>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Reschedule Dialog */}
        <Dialog
          open={rescheduleDialogOpen}
          onClose={handleCloseDialogs}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              maxHeight: "90vh",
            },
          }}
        >
          <DialogTitle>
            <MDBox display="flex" justifyContent="space-between" alignItems="center">
              <MDTypography variant="h5" fontWeight="bold">
                Reschedule Inspection
              </MDTypography>
              <IconButton onClick={handleCloseDialogs} disabled={submitting}>
                <Icon>close</Icon>
              </IconButton>
            </MDBox>
          </DialogTitle>
          <DialogContent dividers>
            {selectedInspection && (
              <Grid container spacing={3}>
                {/* Left Column - Form */}
                <Grid item xs={12} md={4}>
                  <MDBox>
                    <MDTypography variant="body2" color="text.secondary" mb={2}>
                      Rescheduling inspection for:{" "}
                      <strong>{selectedInspection.propertyTitle}</strong>
                    </MDTypography>

                    {dialogMessage.text && (
                      <Alert
                        severity={
                          dialogMessage.type === "success"
                            ? "success"
                            : dialogMessage.type === "error"
                            ? "error"
                            : "info"
                        }
                        sx={{ mb: 2 }}
                      >
                        {dialogMessage.text}
                      </Alert>
                    )}

                    <MDBox mb={2}>
                      <MDTypography
                        variant="caption"
                        color="text.secondary"
                        fontWeight="medium"
                        display="block"
                        mb={0.5}
                      >
                        Inspector (Current)
                      </MDTypography>
                      <MDTypography variant="body2" fontWeight="medium">
                        {selectedInspection.inspector}
                      </MDTypography>
                    </MDBox>

                    <MDBox mb={2}>
                      <MDTypography
                        variant="caption"
                        color="text.secondary"
                        fontWeight="medium"
                        display="block"
                        mb={0.5}
                      >
                        New Inspection Date & Time *
                      </MDTypography>
                      <TextField
                        fullWidth
                        type="datetime-local"
                        name="inspectionDate"
                        value={formData.inspectionDate}
                        onChange={handleFormChange}
                        disabled={submitting}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            height: "44.3px",
                            "& input": {
                              padding: "12px 14px",
                            },
                          },
                        }}
                      />
                    </MDBox>

                    <MDBox mb={2}>
                      <MDTypography
                        variant="caption"
                        color="text.secondary"
                        fontWeight="medium"
                        display="block"
                        mb={0.5}
                      >
                        Inspection Location *
                      </MDTypography>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        name="inspectionLocation"
                        value={formData.inspectionLocation}
                        onChange={handleFormChange}
                        disabled={submitting}
                        placeholder="Enter inspection location with Google Maps link"
                      />
                    </MDBox>
                  </MDBox>
                </Grid>

                {/* Right Column - Calendar */}
                <Grid item xs={12} md={8}>
                  <MDBox>
                    <MDBox mb={2} display="flex" justifyContent="space-between" alignItems="center">
                      <MDTypography variant="h6" fontWeight="bold">
                        Inspector Calendar
                      </MDTypography>
                      <ButtonGroup size="small" variant="outlined">
                        <MDButton
                          variant={calendarView === "month" ? "contained" : "outlined"}
                          color="info"
                          onClick={() => handleViewChange("month")}
                        >
                          Month
                        </MDButton>
                        <MDButton
                          variant={calendarView === "week" ? "contained" : "outlined"}
                          color="info"
                          onClick={() => handleViewChange("week")}
                        >
                          Week
                        </MDButton>
                        <MDButton
                          variant={calendarView === "day" ? "contained" : "outlined"}
                          color="info"
                          onClick={() => handleViewChange("day")}
                        >
                          Day
                        </MDButton>
                      </ButtonGroup>
                    </MDBox>

                    <MDBox mb={2} display="flex" gap={1} alignItems="center">
                      <MDButton
                        size="small"
                        onClick={() => navigateCalendar("PREV")}
                        startIcon={<Icon>chevron_left</Icon>}
                      >
                        Prev
                      </MDButton>
                      <MDButton size="small" onClick={() => setCalendarDate(new Date())}>
                        Today
                      </MDButton>
                      <MDButton
                        size="small"
                        onClick={() => navigateCalendar("NEXT")}
                        endIcon={<Icon>chevron_right</Icon>}
                      >
                        Next
                      </MDButton>
                    </MDBox>

                    {inspectorCalendar ? (
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          bgcolor: "background.default",
                          borderRadius: 2,
                          height: 600,
                          overflow: "visible",
                          position: "relative",
                          "& .rbc-calendar": {
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                          },
                          "& .rbc-time-view": {
                            border: "none",
                          },
                          "& .rbc-time-header": {
                            border: "1px solid #e0e0e0",
                          },
                          "& .rbc-time-content": {
                            borderTop: "2px solid #e0e0e0",
                          },
                          "& .rbc-slot-reserved": {
                            backgroundColor: "rgba(244, 67, 54, 0.15) !important",
                            border: "1px dashed rgba(244, 67, 54, 0.5) !important",
                            cursor: "not-allowed !important",
                            pointerEvents: "none !important",
                          },
                          "& .rbc-event": {
                            padding: "6px 10px",
                            fontSize: "0.875rem",
                            borderRadius: "4px",
                          },
                        }}
                      >
                        <Calendar
                          localizer={localizer}
                          events={getCalendarEvents()}
                          startAccessor="start"
                          endAccessor="end"
                          style={{ height: "100%", minHeight: "550px" }}
                          view={calendarView}
                          date={calendarDate}
                          onView={handleViewChange}
                          onNavigate={setCalendarDate}
                          onSelectEvent={handleSelectEvent}
                          onSelectSlot={handleSelectSlot}
                          selectable
                          eventPropGetter={eventStyleGetter}
                          slotPropGetter={slotPropGetter}
                          popup
                          showMultiDayTimes
                          step={60}
                          timeslots={1}
                          components={{
                            event: ({ event }) => {
                              const isExisting = event.resource?.isExisting;
                              const isSelected = event.resource?.isSelected;
                              const eventStyle = eventStyleGetter(event);

                              const handleMouseEnter = (e) => {
                                if (isExisting) {
                                  const eventRect = e.currentTarget.getBoundingClientRect();
                                  const calendarPaper =
                                    e.currentTarget.closest('[class*="MuiPaper"]');
                                  const calendarRect = calendarPaper?.getBoundingClientRect();

                                  if (calendarRect) {
                                    const top = eventRect.top - calendarRect.top - 8;
                                    const left = eventRect.left - calendarRect.left;
                                    setModalPosition({ top, left });
                                  }

                                  setHoveredEvent({
                                    type: "existing",
                                    inspection: event.resource,
                                    start: event.start,
                                    end: event.end,
                                  });
                                  setHoverModalOpen(true);
                                }
                              };

                              const handleMouseLeave = () => {
                                if (isExisting) {
                                  setHoverModalOpen(false);
                                }
                              };

                              return (
                                <div
                                  data-existing={isExisting ? "true" : "false"}
                                  data-selected={isSelected ? "true" : "false"}
                                  style={{
                                    ...eventStyle.style,
                                    cursor: isExisting
                                      ? "pointer"
                                      : isSelected
                                      ? "pointer"
                                      : "default",
                                    width: "100%",
                                    height: "100%",
                                  }}
                                  onClick={(e) => {
                                    if (isExisting) {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      handleSelectEvent(event);
                                    }
                                  }}
                                  onMouseEnter={handleMouseEnter}
                                  onMouseLeave={handleMouseLeave}
                                  title={
                                    isExisting
                                      ? `${String(
                                          event.title || "Existing Inspection"
                                        )} - Hover to view details`
                                      : String(event.title || "Inspection")
                                  }
                                >
                                  {event.title && String(event.title).trim() !== "" ? (
                                    <span
                                      style={{
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        display: "block",
                                        width: "100%",
                                      }}
                                    >
                                      {String(event.title)}
                                    </span>
                                  ) : null}
                                </div>
                              );
                            },
                          }}
                        />

                        {/* Hover Modal for Existing Inspections */}
                        {hoverModalOpen && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: `${modalPosition.top}px`,
                              left: `${modalPosition.left}px`,
                              zIndex: 9999,
                              minWidth: "320px",
                              maxWidth: "400px",
                              pointerEvents: "auto",
                              transform: "translateY(-100%)",
                              animation: "fadeInDown 0.2s ease-in",
                              "@keyframes fadeInDown": {
                                from: {
                                  opacity: 0,
                                  transform: "translateY(calc(-100% - 10px))",
                                },
                                to: {
                                  opacity: 1,
                                  transform: "translateY(-100%)",
                                },
                              },
                            }}
                            onMouseLeave={() => setHoverModalOpen(false)}
                          >
                            <Paper
                              elevation={8}
                              sx={{
                                bgcolor: "white",
                                color: "text.primary",
                                borderRadius: 2,
                                p: 2,
                                boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                                position: "relative",
                                border: "1px solid #e0e0e0",
                              }}
                            >
                              {hoveredEvent?.type === "existing" && hoveredEvent.inspection ? (
                                <MDBox>
                                  <MDBox
                                    mb={1.5}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                  >
                                    <MDTypography
                                      variant="h6"
                                      fontWeight="bold"
                                      sx={{ color: "text.primary", fontSize: "1rem" }}
                                      display="flex"
                                      alignItems="center"
                                    >
                                      <Icon
                                        sx={{ mr: 0.5, fontSize: "1.2rem", color: "info.main" }}
                                      >
                                        info
                                      </Icon>
                                      Inspection Details
                                    </MDTypography>
                                  </MDBox>
                                  <Divider sx={{ bgcolor: "#e0e0e0", mb: 1.5 }} />
                                  <MDBox mb={1.5}>
                                    <MDTypography
                                      variant="caption"
                                      sx={{
                                        color: "text.secondary",
                                        fontWeight: 600,
                                        fontSize: "0.7rem",
                                      }}
                                      display="block"
                                      mb={0.5}
                                    >
                                      Property
                                    </MDTypography>
                                    <MDTypography
                                      variant="body2"
                                      sx={{ color: "text.primary", fontWeight: 600 }}
                                    >
                                      {hoveredEvent.inspection?.propertyTitle ||
                                        hoveredEvent.resource?.title ||
                                        "N/A"}
                                    </MDTypography>
                                  </MDBox>
                                  <MDBox mb={1.5}>
                                    <MDTypography
                                      variant="caption"
                                      sx={{
                                        color: "text.secondary",
                                        fontWeight: 600,
                                        fontSize: "0.7rem",
                                      }}
                                      display="block"
                                      mb={0.5}
                                    >
                                      Date & Time
                                    </MDTypography>
                                    <MDTypography variant="body2" sx={{ color: "text.primary" }}>
                                      {hoveredEvent.start
                                        ? `${new Date(hoveredEvent.start).toLocaleDateString(
                                            "en-US",
                                            {
                                              weekday: "short",
                                              month: "short",
                                              day: "numeric",
                                            }
                                          )}`
                                        : "N/A"}
                                    </MDTypography>
                                    <MDTypography
                                      variant="caption"
                                      sx={{ color: "text.secondary", fontSize: "0.75rem" }}
                                    >
                                      {hoveredEvent.start && hoveredEvent.end
                                        ? `${new Date(hoveredEvent.start).toLocaleTimeString(
                                            "en-US",
                                            {
                                              hour: "2-digit",
                                              minute: "2-digit",
                                            }
                                          )} - ${new Date(hoveredEvent.end).toLocaleTimeString(
                                            "en-US",
                                            {
                                              hour: "2-digit",
                                              minute: "2-digit",
                                            }
                                          )}`
                                        : "N/A"}
                                    </MDTypography>
                                  </MDBox>
                                  <MDBox mb={1.5}>
                                    <MDTypography
                                      variant="caption"
                                      sx={{
                                        color: "text.secondary",
                                        fontWeight: 600,
                                        fontSize: "0.7rem",
                                      }}
                                      display="block"
                                      mb={0.5}
                                    >
                                      Status
                                    </MDTypography>
                                    <Chip
                                      label={hoveredEvent.inspection.status || "N/A"}
                                      size="small"
                                      color={
                                        ["Inspection", "First_Inspection"].includes(
                                          hoveredEvent.inspection.status
                                        )
                                          ? "info"
                                          : ["Inspection_Offer", "Inspection_Completed"].includes(
                                              hoveredEvent.inspection.status
                                            )
                                          ? "success"
                                          : "default"
                                      }
                                      sx={{ fontWeight: 600 }}
                                    />
                                  </MDBox>
                                  {hoveredEvent.inspection.location && (
                                    <MDBox>
                                      <MDTypography
                                        variant="caption"
                                        sx={{
                                          color: "text.secondary",
                                          fontWeight: 600,
                                          fontSize: "0.7rem",
                                        }}
                                        display="block"
                                        mb={0.5}
                                      >
                                        Location
                                      </MDTypography>
                                      <MDTypography
                                        variant="body2"
                                        sx={{
                                          whiteSpace: "pre-wrap",
                                          wordBreak: "break-word",
                                          color: "text.primary",
                                          fontSize: "0.75rem",
                                        }}
                                      >
                                        {hoveredEvent.inspection.location.length > 100
                                          ? `${hoveredEvent.inspection.location.substring(
                                              0,
                                              100
                                            )}...`
                                          : hoveredEvent.inspection.location}
                                      </MDTypography>
                                    </MDBox>
                                  )}
                                </MDBox>
                              ) : null}
                            </Paper>
                          </Box>
                        )}
                      </Paper>
                    ) : (
                      <MDBox
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        minHeight="600px"
                      >
                        <CircularProgress />
                      </MDBox>
                    )}
                  </MDBox>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <MDButton onClick={handleCloseDialogs} disabled={submitting}>
              Cancel
            </MDButton>
            <MDButton
              variant="gradient"
              color="warning"
              onClick={handleReschedule}
              disabled={submitting || !formData.inspectionDate || !formData.inspectionLocation}
              startIcon={submitting ? <CircularProgress size={16} /> : <Icon>event</Icon>}
            >
              {submitting ? "Rescheduling..." : "Reschedule"}
            </MDButton>
          </DialogActions>
        </Dialog>

        {/* Reassign Inspector Dialog */}
        <Dialog
          open={reassignDialogOpen}
          onClose={handleCloseDialogs}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              maxHeight: "90vh",
            },
          }}
        >
          <DialogTitle>
            <MDBox display="flex" justifyContent="space-between" alignItems="center">
              <MDTypography variant="h5" fontWeight="bold">
                Reassign Inspector
              </MDTypography>
              <IconButton onClick={handleCloseDialogs} disabled={submitting}>
                <Icon>close</Icon>
              </IconButton>
            </MDBox>
          </DialogTitle>
          <DialogContent dividers>
            {selectedInspection && (
              <Grid container spacing={3}>
                {/* Left Column - Form */}
                <Grid item xs={12} md={4}>
                  <MDBox>
                    <MDTypography variant="body2" color="text.secondary" mb={2}>
                      Reassigning inspection for:{" "}
                      <strong>{selectedInspection.propertyTitle}</strong>
                    </MDTypography>

                    {dialogMessage.text && (
                      <Alert
                        severity={
                          dialogMessage.type === "success"
                            ? "success"
                            : dialogMessage.type === "error"
                            ? "error"
                            : "info"
                        }
                        sx={{ mb: 2 }}
                      >
                        {dialogMessage.text}
                      </Alert>
                    )}

                    <MDBox mb={2}>
                      <MDTypography
                        variant="caption"
                        color="text.secondary"
                        fontWeight="medium"
                        display="block"
                        mb={0.5}
                      >
                        Current Inspector
                      </MDTypography>
                      <MDTypography variant="body2" fontWeight="medium">
                        {selectedInspection.inspector}
                      </MDTypography>
                    </MDBox>

                    <MDBox mb={2}>
                      <FormControl fullWidth>
                        <MDTypography
                          variant="caption"
                          color="text.secondary"
                          fontWeight="medium"
                          display="block"
                          mb={0.5}
                        >
                          New Inspector *
                        </MDTypography>
                        <Select
                          name="inspectorId"
                          value={formData.inspectorId}
                          onChange={handleFormChange}
                          disabled={submitting}
                          displayEmpty={false}
                          renderValue={(selected) => {
                            if (!selected) return "";
                            const inspector = inspectors.find((i) => (i._id || i.id) === selected);
                            return inspector ? inspector.fullName : "";
                          }}
                          sx={{
                            height: "44.3px",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "rgba(0, 0, 0, 0.23)",
                            },
                          }}
                        >
                          {inspectors.map((inspector) => (
                            <MenuItem
                              key={inspector._id || inspector.id}
                              value={inspector._id || inspector.id}
                            >
                              <MDBox display="flex" alignItems="center" gap={1}>
                                <Icon sx={{ fontSize: "1.2rem", color: "info.main" }}>person</Icon>
                                <MDBox>
                                  <MDTypography variant="body2" fontWeight="medium">
                                    {inspector.fullName}
                                  </MDTypography>
                                  <MDTypography variant="caption" color="text.secondary">
                                    {inspector.email}
                                  </MDTypography>
                                </MDBox>
                              </MDBox>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </MDBox>

                    <MDBox mb={2}>
                      <MDTypography
                        variant="caption"
                        color="text.secondary"
                        fontWeight="medium"
                        display="block"
                        mb={0.5}
                      >
                        Inspection Date & Time *
                      </MDTypography>
                      <TextField
                        fullWidth
                        type="datetime-local"
                        name="inspectionDate"
                        value={formData.inspectionDate}
                        onChange={handleFormChange}
                        disabled={submitting || !formData.inspectorId}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            height: "44.3px",
                            "& input": {
                              padding: "12px 14px",
                            },
                          },
                        }}
                      />
                    </MDBox>

                    <MDBox mb={2}>
                      <MDTypography
                        variant="caption"
                        color="text.secondary"
                        fontWeight="medium"
                        display="block"
                        mb={0.5}
                      >
                        Inspection Location *
                      </MDTypography>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        name="inspectionLocation"
                        value={formData.inspectionLocation}
                        onChange={handleFormChange}
                        disabled={submitting}
                        placeholder="Enter inspection location with Google Maps link"
                      />
                    </MDBox>
                  </MDBox>
                </Grid>

                {/* Right Column - Calendar */}
                <Grid item xs={12} md={8}>
                  <MDBox>
                    {formData.inspectorId ? (
                      <>
                        <MDBox
                          mb={2}
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <MDTypography variant="h6" fontWeight="bold">
                            Inspector Calendar
                          </MDTypography>
                          <ButtonGroup size="small" variant="outlined">
                            <MDButton
                              variant={calendarView === "month" ? "contained" : "outlined"}
                              color="info"
                              onClick={() => handleViewChange("month")}
                            >
                              Month
                            </MDButton>
                            <MDButton
                              variant={calendarView === "week" ? "contained" : "outlined"}
                              color="info"
                              onClick={() => handleViewChange("week")}
                            >
                              Week
                            </MDButton>
                            <MDButton
                              variant={calendarView === "day" ? "contained" : "outlined"}
                              color="info"
                              onClick={() => handleViewChange("day")}
                            >
                              Day
                            </MDButton>
                          </ButtonGroup>
                        </MDBox>

                        <MDBox mb={2} display="flex" gap={1} alignItems="center">
                          <MDButton
                            size="small"
                            onClick={() => navigateCalendar("PREV")}
                            startIcon={<Icon>chevron_left</Icon>}
                          >
                            Prev
                          </MDButton>
                          <MDButton size="small" onClick={() => setCalendarDate(new Date())}>
                            Today
                          </MDButton>
                          <MDButton
                            size="small"
                            onClick={() => navigateCalendar("NEXT")}
                            endIcon={<Icon>chevron_right</Icon>}
                          >
                            Next
                          </MDButton>
                        </MDBox>

                        {inspectorCalendar ? (
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2,
                              bgcolor: "background.default",
                              borderRadius: 2,
                              height: 600,
                              overflow: "visible",
                              position: "relative",
                              "& .rbc-calendar": {
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                              },
                              "& .rbc-time-view": {
                                border: "none",
                              },
                              "& .rbc-time-header": {
                                border: "1px solid #e0e0e0",
                              },
                              "& .rbc-time-content": {
                                borderTop: "2px solid #e0e0e0",
                              },
                              "& .rbc-slot-reserved": {
                                backgroundColor: "rgba(244, 67, 54, 0.15) !important",
                                border: "1px dashed rgba(244, 67, 54, 0.5) !important",
                                cursor: "not-allowed !important",
                                pointerEvents: "none !important",
                              },
                              "& .rbc-event": {
                                padding: "6px 10px",
                                fontSize: "0.875rem",
                                borderRadius: "4px",
                              },
                            }}
                          >
                            <Calendar
                              localizer={localizer}
                              events={getCalendarEvents()}
                              startAccessor="start"
                              endAccessor="end"
                              style={{ height: "100%", minHeight: "550px" }}
                              view={calendarView}
                              date={calendarDate}
                              onView={handleViewChange}
                              onNavigate={setCalendarDate}
                              onSelectEvent={handleSelectEvent}
                              onSelectSlot={handleSelectSlot}
                              selectable
                              eventPropGetter={eventStyleGetter}
                              slotPropGetter={slotPropGetter}
                              popup
                              showMultiDayTimes
                              step={60}
                              timeslots={1}
                              components={{
                                event: ({ event }) => {
                                  const isExisting = event.resource?.isExisting;
                                  const isSelected = event.resource?.isSelected;
                                  const eventStyle = eventStyleGetter(event);

                                  const handleMouseEnter = (e) => {
                                    if (isExisting) {
                                      const eventRect = e.currentTarget.getBoundingClientRect();
                                      const calendarPaper =
                                        e.currentTarget.closest('[class*="MuiPaper"]');
                                      const calendarRect = calendarPaper?.getBoundingClientRect();

                                      if (calendarRect) {
                                        const top = eventRect.top - calendarRect.top - 8;
                                        const left = eventRect.left - calendarRect.left;
                                        setModalPosition({ top, left });
                                      }

                                      setHoveredEvent({
                                        type: "existing",
                                        inspection: event.resource,
                                        start: event.start,
                                        end: event.end,
                                      });
                                      setHoverModalOpen(true);
                                    }
                                  };

                                  const handleMouseLeave = () => {
                                    if (isExisting) {
                                      setHoverModalOpen(false);
                                    }
                                  };

                                  return (
                                    <div
                                      data-existing={isExisting ? "true" : "false"}
                                      data-selected={isSelected ? "true" : "false"}
                                      style={{
                                        ...eventStyle.style,
                                        cursor: isExisting
                                          ? "pointer"
                                          : isSelected
                                          ? "pointer"
                                          : "default",
                                        width: "100%",
                                        height: "100%",
                                      }}
                                      onClick={(e) => {
                                        if (isExisting) {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          handleSelectEvent(event);
                                        }
                                      }}
                                      onMouseEnter={handleMouseEnter}
                                      onMouseLeave={handleMouseLeave}
                                      title={
                                        isExisting
                                          ? `${String(
                                              event.title || "Existing Inspection"
                                            )} - Hover to view details`
                                          : String(event.title || "Inspection")
                                      }
                                    >
                                      {event.title && String(event.title).trim() !== "" ? (
                                        <span
                                          style={{
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            display: "block",
                                            width: "100%",
                                          }}
                                        >
                                          {String(event.title)}
                                        </span>
                                      ) : null}
                                    </div>
                                  );
                                },
                              }}
                            />

                            {/* Hover Modal for Existing Inspections */}
                            {hoverModalOpen && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: `${modalPosition.top}px`,
                                  left: `${modalPosition.left}px`,
                                  zIndex: 9999,
                                  minWidth: "320px",
                                  maxWidth: "400px",
                                  pointerEvents: "auto",
                                  transform: "translateY(-100%)",
                                  animation: "fadeInDown 0.2s ease-in",
                                  "@keyframes fadeInDown": {
                                    from: {
                                      opacity: 0,
                                      transform: "translateY(calc(-100% - 10px))",
                                    },
                                    to: {
                                      opacity: 1,
                                      transform: "translateY(-100%)",
                                    },
                                  },
                                }}
                                onMouseLeave={() => setHoverModalOpen(false)}
                              >
                                <Paper
                                  elevation={8}
                                  sx={{
                                    bgcolor: "white",
                                    color: "text.primary",
                                    borderRadius: 2,
                                    p: 2,
                                    boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                                    position: "relative",
                                    border: "1px solid #e0e0e0",
                                  }}
                                >
                                  {hoveredEvent?.type === "existing" && hoveredEvent.inspection ? (
                                    <MDBox>
                                      <MDBox
                                        mb={1.5}
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                      >
                                        <MDTypography
                                          variant="h6"
                                          fontWeight="bold"
                                          sx={{ color: "text.primary", fontSize: "1rem" }}
                                          display="flex"
                                          alignItems="center"
                                        >
                                          <Icon
                                            sx={{ mr: 0.5, fontSize: "1.2rem", color: "info.main" }}
                                          >
                                            info
                                          </Icon>
                                          Inspection Details
                                        </MDTypography>
                                      </MDBox>
                                      <Divider sx={{ bgcolor: "#e0e0e0", mb: 1.5 }} />
                                      <MDBox mb={1.5}>
                                        <MDTypography
                                          variant="caption"
                                          sx={{
                                            color: "text.secondary",
                                            fontWeight: 600,
                                            fontSize: "0.7rem",
                                          }}
                                          display="block"
                                          mb={0.5}
                                        >
                                          Property
                                        </MDTypography>
                                        <MDTypography
                                          variant="body2"
                                          sx={{ color: "text.primary", fontWeight: 600 }}
                                        >
                                          {hoveredEvent.inspection?.propertyTitle ||
                                            hoveredEvent.resource?.title ||
                                            "N/A"}
                                        </MDTypography>
                                      </MDBox>
                                      <MDBox mb={1.5}>
                                        <MDTypography
                                          variant="caption"
                                          sx={{
                                            color: "text.secondary",
                                            fontWeight: 600,
                                            fontSize: "0.7rem",
                                          }}
                                          display="block"
                                          mb={0.5}
                                        >
                                          Date & Time
                                        </MDTypography>
                                        <MDTypography
                                          variant="body2"
                                          sx={{ color: "text.primary" }}
                                        >
                                          {hoveredEvent.start
                                            ? `${new Date(hoveredEvent.start).toLocaleDateString(
                                                "en-US",
                                                {
                                                  weekday: "short",
                                                  month: "short",
                                                  day: "numeric",
                                                }
                                              )}`
                                            : "N/A"}
                                        </MDTypography>
                                        <MDTypography
                                          variant="caption"
                                          sx={{ color: "text.secondary", fontSize: "0.75rem" }}
                                        >
                                          {hoveredEvent.start && hoveredEvent.end
                                            ? `${new Date(hoveredEvent.start).toLocaleTimeString(
                                                "en-US",
                                                {
                                                  hour: "2-digit",
                                                  minute: "2-digit",
                                                }
                                              )} - ${new Date(hoveredEvent.end).toLocaleTimeString(
                                                "en-US",
                                                {
                                                  hour: "2-digit",
                                                  minute: "2-digit",
                                                }
                                              )}`
                                            : "N/A"}
                                        </MDTypography>
                                      </MDBox>
                                      <MDBox mb={1.5}>
                                        <MDTypography
                                          variant="caption"
                                          sx={{
                                            color: "text.secondary",
                                            fontWeight: 600,
                                            fontSize: "0.7rem",
                                          }}
                                          display="block"
                                          mb={0.5}
                                        >
                                          Status
                                        </MDTypography>
                                        <Chip
                                          label={hoveredEvent.inspection.status || "N/A"}
                                          size="small"
                                          color={
                                            hoveredEvent.inspection.status === "Inspection"
                                              ? "info"
                                              : hoveredEvent.inspection.status ===
                                                "Inspection_Completed"
                                              ? "success"
                                              : "default"
                                          }
                                          sx={{ fontWeight: 600 }}
                                        />
                                      </MDBox>
                                      {hoveredEvent.inspection.location && (
                                        <MDBox>
                                          <MDTypography
                                            variant="caption"
                                            sx={{
                                              color: "text.secondary",
                                              fontWeight: 600,
                                              fontSize: "0.7rem",
                                            }}
                                            display="block"
                                            mb={0.5}
                                          >
                                            Location
                                          </MDTypography>
                                          <MDTypography
                                            variant="body2"
                                            sx={{
                                              whiteSpace: "pre-wrap",
                                              wordBreak: "break-word",
                                              color: "text.primary",
                                              fontSize: "0.75rem",
                                            }}
                                          >
                                            {hoveredEvent.inspection.location.length > 100
                                              ? `${hoveredEvent.inspection.location.substring(
                                                  0,
                                                  100
                                                )}...`
                                              : hoveredEvent.inspection.location}
                                          </MDTypography>
                                        </MDBox>
                                      )}
                                    </MDBox>
                                  ) : null}
                                </Paper>
                              </Box>
                            )}
                          </Paper>
                        ) : (
                          <MDBox
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            minHeight="600px"
                          >
                            <CircularProgress />
                          </MDBox>
                        )}
                      </>
                    ) : (
                      <MDBox
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        minHeight="600px"
                        sx={{
                          bgcolor: "grey.50",
                          borderRadius: 2,
                        }}
                      >
                        <MDBox textAlign="center">
                          <Icon sx={{ fontSize: "4rem", color: "text.secondary", mb: 2 }}>
                            person_search
                          </Icon>
                          <MDTypography variant="h6" color="text.secondary" mb={1}>
                            Select an Inspector
                          </MDTypography>
                          <MDTypography variant="body2" color="text.secondary">
                            Please select a new inspector to view their calendar
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                    )}
                  </MDBox>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <MDButton onClick={handleCloseDialogs} disabled={submitting}>
              Cancel
            </MDButton>
            <MDButton
              variant="gradient"
              color="secondary"
              onClick={handleReassign}
              disabled={
                submitting ||
                !formData.inspectorId ||
                !formData.inspectionDate ||
                !formData.inspectionLocation
              }
              startIcon={submitting ? <CircularProgress size={16} /> : <Icon>swap_horiz</Icon>}
            >
              {submitting ? "Reassigning..." : "Reassign"}
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
    </DashboardLayout>
  );
}

export default Inspections;
